// Viewport Photo Loader
// Calculates which photos should be loaded based on viewport and loads them in batches
export class ViewportPhotoLoader {
  constructor(photoService) {
    this.photoService = photoService;
    this.loadBuffer = 0.3; // Load 30% more on each side
  }

  /**
   * Calculate which photos need to be loaded based on viewport
   * @param {Array<object>} allPhotoMetadata - Array of all photo basic metadata (id, filename, thumb, processing_status)
   * @param {number} startIndex - First visible photo index
   * @param {number} endIndex - Last visible photo index (inclusive)
   * @param {number|null} activePhotoId - Currently active photo (must be loaded even if not visible)
   * @returns {Array<number>} - Array of photo IDs that need to be loaded
   */
  calculatePhotosToLoad(allPhotoMetadata, startIndex, endIndex, activePhotoId = null) {
    if (allPhotoMetadata.length === 0) return [];

    const totalPhotos = allPhotoMetadata.length;
    const visibleCount = endIndex - startIndex + 1;
    
    // Calculate buffer size (30% on each side)
    const bufferSize = Math.ceil(visibleCount * this.loadBuffer);
    
    // Calculate extended range
    const extendedStart = Math.max(0, startIndex - bufferSize);
    const extendedEnd = Math.min(totalPhotos - 1, endIndex + bufferSize);
    
    // Get photo IDs in extended range that don't have full data
    const photoIdsToLoad = [];
    
    for (let i = extendedStart; i <= extendedEnd; i++) {
      const photoId = allPhotoMetadata[i].id;
      // Only add if not already fully loaded in service
      if (!this.photoService.hasFullData(photoId)) {
        photoIdsToLoad.push(photoId);
      }
    }
    
    // Ensure active photo is included if provided and not already fully loaded
    if (activePhotoId && !this.photoService.hasFullData(activePhotoId)) {
      if (!photoIdsToLoad.includes(activePhotoId)) {
        photoIdsToLoad.push(activePhotoId);
      }
    }
    
    return photoIdsToLoad;
  }

  /**
   * Load photos in batches
   * @param {Array<number>} photoIds - Array of photo IDs to load
   * @returns {Promise<Array<object>>} - Array of loaded photo data
   */
  async loadPhotos(photoIds) {
    if (photoIds.length === 0) return [];

    console.log(`[ViewportPhotoLoader] Loading ${photoIds.length} photos`);
    
    // Use PhotoService to load (it handles batching and deduplication)
    const photos = await this.photoService.getFullInfo(photoIds);
    
    console.log(`[ViewportPhotoLoader] Loaded ${photos.length} photos. Cache stats:`, this.photoService.getStats());
    
    return photos;
  }

  /**
   * Preload photos for a specific index range
   * Useful for initial load or when jumping to a specific position
   * @param {Array<object>} allPhotoMetadata - Array of all photo basic metadata
   * @param {number} centerIndex - Index to center the preload around
   * @param {number} visibleCount - Number of photos visible at once
   * @param {number|null} activePhotoId - Currently active photo
   * @returns {Promise<Array<object>>}
   */
  async preloadForIndex(allPhotoMetadata, centerIndex, visibleCount, activePhotoId = null) {
    const halfVisible = Math.floor(visibleCount / 2);
    const startIndex = Math.max(0, centerIndex - halfVisible);
    const endIndex = Math.min(allPhotoMetadata.length - 1, centerIndex + halfVisible);
    
    const photoIds = this.calculatePhotosToLoad(
      allPhotoMetadata,
      startIndex,
      endIndex,
      activePhotoId
    );
    
    return await this.loadPhotos(photoIds);
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return this.photoService.getStats();
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.photoService.clear();
  }
}

